/**
 * Finding Type Labels, Icons, and Colors
 * 
 * Provides Swedish translations and visual styling for finding types
 * used in the audit findings UI.
 */

import {
  Clock,
  Percent,
  AlertTriangle,
  XCircle,
  TrendingUp,
  TrendingDown,
  Scale,
  Copy,
  AlertCircle,
  Shield,
  Lock,
  Flag,
  Database,
  FileText,
  Calendar,
  CreditCard,
  Receipt,
  type LucideIcon
} from 'lucide-react';

/**
 * Finding type label configuration
 */
interface FindingTypeConfig {
  label: string;
  icon: LucideIcon;
  colorClass: string;
}

/**
 * Configuration for all known finding types
 */
const FINDING_TYPE_CONFIG: Record<string, FindingTypeConfig> = {
  // Payment-related
  late_payment: {
    label: 'Sen inbetalning',
    icon: Clock,
    colorClass: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
  },
  interest_charge: {
    label: 'Räntedebitering',
    icon: Percent,
    colorClass: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
  },
  penalty_fee: {
    label: 'Förseningsavgift',
    icon: AlertTriangle,
    colorClass: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
  },
  missing_payment: {
    label: 'Saknad inbetalning',
    icon: XCircle,
    colorClass: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
  },
  overpayment: {
    label: 'Överbetalning',
    icon: TrendingUp,
    colorClass: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
  },
  underpayment: {
    label: 'Underbetalning',
    icon: TrendingDown,
    colorClass: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
  },
  
  // Balance and transaction
  balance_discrepancy: {
    label: 'Saldoavvikelse',
    icon: Scale,
    colorClass: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
  },
  duplicate_transaction: {
    label: 'Dubblettransaktion',
    icon: Copy,
    colorClass: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
  },
  unusual_amount: {
    label: 'Ovanligt belopp',
    icon: AlertCircle,
    colorClass: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
  },
  
  // Statistical outliers
  statistical_outlier: {
    label: 'Statistisk avvikelse',
    icon: TrendingUp,
    colorClass: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
  },
  large_deviation_median: {
    label: 'Stor avvikelse från median',
    icon: TrendingUp,
    colorClass: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
  },
  large_deviation_mean: {
    label: 'Stor avvikelse från medel',
    icon: TrendingUp,
    colorClass: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
  },
  amount_outlier: {
    label: 'Beloppsavvikelse',
    icon: TrendingUp,
    colorClass: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
  },
  
  // AI-driven pattern findings
  contextual_deviation: {
    label: 'Kontextuell avvikelse',
    icon: AlertCircle,
    colorClass: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
  },
  sequence_pattern: {
    label: 'Sekvensmönster',
    icon: TrendingUp,
    colorClass: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300'
  },
  semantic_anomaly: {
    label: 'Semantisk anomali',
    icon: AlertTriangle,
    colorClass: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
  },
  pattern_irregularity: {
    label: 'Mönsteravvikelse',
    icon: AlertCircle,
    colorClass: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
  },
  
  // Compliance and control
  compliance_issue: {
    label: 'Regelefterlevnad',
    icon: Shield,
    colorClass: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
  },
  internal_control: {
    label: 'Intern kontroll',
    icon: Lock,
    colorClass: 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-300'
  },
  risk_indicator: {
    label: 'Riskindikator',
    icon: Flag,
    colorClass: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
  },
  
  // Data quality
  data_quality: {
    label: 'Datakvalitet',
    icon: Database,
    colorClass: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
  },
  
  // Tax-specific
  tax_deadline: {
    label: 'Skattedeadline',
    icon: Calendar,
    colorClass: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300'
  },
  tax_payment: {
    label: 'Skatteinbetalning',
    icon: CreditCard,
    colorClass: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
  },
  tax_refund: {
    label: 'Skatteåterbetalning',
    icon: Receipt,
    colorClass: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
  },
  
  // Default
  general: {
    label: 'Övrigt fynd',
    icon: FileText,
    colorClass: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
  }
};

/**
 * Get Swedish label for a finding type
 */
export function getFindingTypeLabel(findingType: string | null | undefined): string {
  if (!findingType) return FINDING_TYPE_CONFIG.general.label;
  const config = FINDING_TYPE_CONFIG[findingType.toLowerCase()];
  return config?.label ?? FINDING_TYPE_CONFIG.general.label;
}

/**
 * Get icon component for a finding type
 */
export function getFindingTypeIcon(findingType: string | null | undefined): LucideIcon {
  if (!findingType) return FINDING_TYPE_CONFIG.general.icon;
  const config = FINDING_TYPE_CONFIG[findingType.toLowerCase()];
  return config?.icon ?? FINDING_TYPE_CONFIG.general.icon;
}

/**
 * Get Tailwind color classes for a finding type
 */
export function getFindingTypeColor(findingType: string | null | undefined): string {
  if (!findingType) return FINDING_TYPE_CONFIG.general.colorClass;
  const config = FINDING_TYPE_CONFIG[findingType.toLowerCase()];
  return config?.colorClass ?? FINDING_TYPE_CONFIG.general.colorClass;
}

/**
 * Check if a finding type is known/configured
 */
export function isKnownFindingType(findingType: string | null | undefined): boolean {
  if (!findingType) return false;
  return findingType.toLowerCase() in FINDING_TYPE_CONFIG;
}

/**
 * Get all configured finding types
 */
export function getAllFindingTypes(): string[] {
  return Object.keys(FINDING_TYPE_CONFIG);
}
